import { useCallback, useEffect, useState } from 'react';

/**
 * API 响应数据类型
 */
interface ApiResponseData {
  dmgUrl?: string;
  url?: string; // Windows 可能使用 url 字段
  version: string;
  productVersion: string;
  sha1hash: string;
  sha256hash: string;
  timestamp: number;
  fileSize: number;
  filename: string;
  commitId: string;
  name: string;
  pub_date: string;
}

/**
 * 下载信息接口
 */
export interface DownloadInfo {
  url: string;
  version: string;
  productVersion: string;
  sha1hash: string;
  sha256hash: string;
  timestamp: number;
  fileSize: number;
  filename: string;
  architecture: string;
  platform: string;
  packageType: string;
  dmgUrl: string;
  commitId: string;
  name: string;
  notes: string;
  pub_date: string;
}

/**
 * 下载信息获取 Hook
 * 负责获取和管理下载信息，根据当前语言动态选择下载地址
 */
export const useDownloadInfo = () => {
  const [downloadInfo, setDownloadInfo] = useState<{
    arm?: DownloadInfo;
    intel?: DownloadInfo;
    windows?: DownloadInfo;
  } | null>(null);
  const [loading, setLoading] = useState(true);

  // 获取下载信息
  const fetchDownloadInfo = useCallback(async () => {
    try {
      setLoading(true);

      // 尝试从实际API获取数据
      try {
        const pathSuffix = 'external';

        const [appleResponse, intelResponse, windowsResponse] = await Promise.all([
          fetch(`/api/proxy/update/api/update/darwin-arm64/stable/${pathSuffix}/1`),
          fetch(`/api/proxy/update/api/update/darwin/stable/${pathSuffix}/1`),
          fetch(`/api/proxy/update/api/update/win32-x64/stable/${pathSuffix}/1`),
        ]);

        if (appleResponse.ok && intelResponse.ok) {
          const appleData = (await appleResponse.json()) as ApiResponseData;
          const intelData = (await intelResponse.json()) as ApiResponseData;

          // 尝试获取Windows数据
          let windowsData: ApiResponseData | null = null;
          if (windowsResponse.ok) {
            try {
              windowsData = (await windowsResponse.json()) as ApiResponseData;
            } catch (error) {
              console.warn('Windows数据解析失败:', error);
            }
          }

          setDownloadInfo({
            arm: {
              url: appleData.dmgUrl || '',
              version: appleData.version,
              productVersion: appleData.productVersion,
              sha1hash: appleData.sha1hash,
              sha256hash: appleData.sha256hash,
              timestamp: appleData.timestamp,
              fileSize: appleData.fileSize,
              filename: appleData.filename,
              architecture: 'arm64',
              platform: 'darwin',
              packageType: 'dmg',
              dmgUrl: appleData.dmgUrl || '',
              commitId: appleData.commitId,
              name: appleData.name,
              notes: 'Apple Silicon 版本',
              pub_date: appleData.pub_date,
            },
            intel: {
              url: intelData.dmgUrl || '',
              version: intelData.version,
              productVersion: intelData.productVersion,
              sha1hash: intelData.sha1hash,
              sha256hash: intelData.sha256hash,
              timestamp: intelData.timestamp,
              fileSize: intelData.fileSize,
              filename: intelData.filename,
              architecture: 'x64',
              platform: 'darwin',
              packageType: 'dmg',
              dmgUrl: intelData.dmgUrl || '',
              commitId: intelData.commitId,
              name: intelData.name,
              notes: 'Intel 版本',
              pub_date: intelData.pub_date,
            },
            windows: windowsData
              ? {
                  url: windowsData.url || windowsData.dmgUrl || '',
                  version: windowsData.version,
                  productVersion: windowsData.productVersion,
                  sha1hash: windowsData.sha1hash,
                  sha256hash: windowsData.sha256hash,
                  timestamp: windowsData.timestamp,
                  fileSize: windowsData.fileSize,
                  filename: windowsData.filename,
                  architecture: 'x64',
                  platform: 'win32',
                  packageType: 'exe',
                  dmgUrl: windowsData.url || windowsData.dmgUrl || '',
                  commitId: windowsData.commitId,
                  name: windowsData.name,
                  notes: 'Windows 版本',
                  pub_date: windowsData.pub_date,
                }
              : undefined,
          });
          return;
        }
      } catch (apiError) {
        console.warn('API请求失败，使用模拟数据', apiError);
      }
    } catch (error) {
      console.error('获取下载信息失败:', error);
      setDownloadInfo(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    // 当组件挂载时重新获取下载信息
    fetchDownloadInfo();
  }, [fetchDownloadInfo]);

  return {
    downloadInfo,
    loading,
    refetch: fetchDownloadInfo,
  };
};
